import { Helpers, HaulingHelper, resolveHelpers, ensureRowIds } from "./helpers";
import Decimal from "decimal.js";

export type TruckingUnit = "Hours" | "Tons" | "Yards" | "Loads";
export const materialUnits = [
  "Ton",
  "Gallon",
  "Pail",
  "Block",
  "Pallet",
  "Truck",
  "Cubic Yd",
  "Pound",
  "Unit",
  "Each",
];
export type CostRow = {
  rowId?: string | null;
  quantitySource?:
    | "manual"
    | "material"
    | "duration"
    | "travel"
    | "duration_travel"
    | "trucking"
    | null;
  hauling?: HaulingHelper | null;
  sourcePlantId?: string | null;
  sourcePlantName?: string | null;
  truckCountDefault?: boolean;
  truckingUnit?: TruckingUnit | null;
  trucksNeeded?: string | null;
  truckCapacity?: string | null;
  truckRateHours?: string | null;
  truckRateTons?: string | null;
  truckRateYards?: string | null;
  truckRateLoads?: string | null;

  sourceType?: "crew" | "equipment" | "additional_cost" | "transfer_truck" | "plant_product" | null;
  sourceId?: string | null;
  sourceName?: string | null;
  name: string;
  category: "material" | "labor" | "trucking" | "equipment" | "other";
  quantity: string;
  hoursPerTruck?: string | null;
  jobSize?: string | null;
  jobSizeUnit?: string | null;
  unit: string;
  rate: string;
  calculation: "manual" | "coverage" | "density" | "thickness_factor";
  factor?: string | null;
  factorAreaUnit?: string | null;
  factorLabel?: string | null;
  thickness?: string | null;
  quantityOverride?: string | null;
};
export type PricingControl = {
  version: 1;
  mode: "automatic" | "rate" | "fixed" | "profit" | "margin" | "legacy_markup";
  value: string;
  discount: string;
};
export type PricingBaseline = {
  pricing?: PricingControl | null;

  pricingMethod: "selling_rate" | "cost_markup" | "fixed";
  markup: string;
  fixedPrice: string;
  profitAmount: string | null;
};
export type Custom = {
  pricing?: PricingControl | null;
  calculationVersion?: number | null;
  helpers?: Helpers | null;
  sourceProductId?: string | null;
  sourceProductRevision?: string | null;
  pricingOverrideField?: "profit" | "margin" | "total" | "rate" | "none" | null;
  overheadPercentage?: string;
  indirectPercentage?: string;
  name: string;
  internalName?: string | null;
  scope?: string | null;
  scopeFormat?: "plain" | "html" | null;
  quantity: string;
  unit: string;
  rate: string;
  pricingMethod: "selling_rate" | "cost_markup" | "fixed";
  markup: string;
  fixedPrice: string;
  profitAmount?: string | null;
  pricingBaseline?: PricingBaseline | null;
  areaSource: "selling_quantity" | "manual";
  area?: string | null;
  areaUnit?: string | null;
  rows: CostRow[];
};
export const blankCustom = (markup = "0"): Custom => ({
  pricing: null,
  calculationVersion: 1,
  helpers: null,
  sourceProductId: null,
  sourceProductRevision: null,
  name: "",
  internalName: null,
  scope: "",
  scopeFormat: "plain",
  quantity: "1",
  unit: "Each",
  rate: "0",
  pricingMethod: "selling_rate",
  markup,
  overheadPercentage: "0",
  indirectPercentage: "0",
  fixedPrice: "0",
  profitAmount: null,
  pricingBaseline: null,
  pricingOverrideField: "none",
  areaSource: "selling_quantity",
  area: "0",
  areaUnit: "sq_ft",
  rows: [],
});
export const sellingUnits = ["Each", "Ton", "Gallon", "Block", "Lin Ft", "Sq Ft", "Sq Yd", "Pail"];
// Display legacy saved units consistently without changing their stored meaning.
export const sellingUnitLabel = (unit: string) => {
  const key = unit
    .trim()
    .toLowerCase()
    .replace(/[_ .]+/g, " ")
    .trim();
  const labels: Record<string, string> = {
    "square feet": "Sq Ft",
    "square foot": "Sq Ft",
    "sq ft": "Sq Ft",
    "square yards": "Sq Yds",
    "square yard": "Sq Yds",
    "sq yd": "Sq Yds",
    "sq yds": "Sq Yds",
    "linear feet": "Lin Ft",
    "linear foot": "Lin Ft",
    "lin ft": "Lin Ft",
    each: "Each",
    tons: "Ton",
    ton: "Ton",
    gallons: "Gallon",
    gallon: "Gallon",
    blocks: "Block",
    block: "Block",
    pails: "Pail",
    pail: "Pail",
  };
  return labels[key] ?? unit;
};
export const measurementUnit = (unit: string) => {
  const key = unit.trim().toLowerCase();
  if (["sq_ft", "sq ft", "square feet", "square foot"].includes(key)) return "sq_ft";
  if (["sq_yd", "sq yd", "square yards", "square yard"].includes(key)) return "sq_yd";
  if (["lin_ft", "lin ft", "linear feet", "linear foot"].includes(key)) return "lin_ft";
  return null;
};
const D = Decimal.clone({ precision: 40, rounding: Decimal.ROUND_HALF_UP });
const number = (value: string | null | undefined) => {
  if (!/^(?:\d{1,10}(?:\.\d{0,8})?|\.\d{1,8})$/.test(value ?? ""))
    throw Error("Use a nonnegative number with at most eight decimal places.");
  return new D(value!);
};
const areaFactor = (unit: string) => {
  switch (unit.trim().toLowerCase()) {
    case "sq_ft":
    case "sq ft":
    case "square foot":
    case "square feet":
      return 1;
    case "sq_yd":
    case "sq yd":
    case "square yard":
    case "square yards":
      return 9;
    default:
      throw Error("Select square feet or square yards for the measured area.");
  }
};
export function rowQuantity(
  item: Custom,
  row: CostRow
): { quantity: Decimal; formula: string; numerator: Decimal; denominator: Decimal } {
  if (row.calculation === "manual") {
    const truck = row.category === "trucking" ? normalizeTrucking(row) : null;
    const count = truck?.truckingUnit === "Hours" ? number(truck.trucksNeeded) : number("1");
    if (truck && !truckingUnits.includes(truck.truckingUnit!))
      throw Error("Choose a trucking unit.");
    if (truck?.trucksNeeded != null && truck.trucksNeeded !== "") number(truck.trucksNeeded);
    const enteredQuantity = number(truck?.quantity ?? row.quantity).mul(count);
    const quantity = ["labor", "material"].includes(row.category)
      ? enteredQuantity.toDecimalPlaces(2)
      : enteredQuantity;
    return {
      quantity,
      numerator: quantity,
      denominator: new D(1),
      formula: truck
        ? truck.truckingUnit === "Hours"
          ? `${truck.trucksNeeded} trucks × ${
              truck.quantity
            } hours per truck = ${quantity} total hours; × $${truck.rate} = ${money(
              quantity.mul(number(truck.rate)).mul(100)
            )}`
          : `${truck.quantity} ${truck.truckingUnit} × $${truck.rate} = ${money(
              quantity.mul(number(truck.rate)).mul(100)
            )}`
        : "Manual quantity",
    };
  }

  if (row.category !== "material")
    throw Error("Quantity helpers are available for materials only.");
  const linkedUnit = measurementUnit(item.unit);
  const useSelling =
    row.calculation === "coverage" ? !!linkedUnit : item.areaSource === "selling_quantity";
  const sourceUnit =
    row.jobSizeUnit ?? (useSelling ? linkedUnit ?? item.unit : item.areaUnit ?? "sq_ft");
  const linear = sourceUnit === "lin_ft";
  if (linear && row.calculation !== "coverage")
    throw Error("Weight calculations require an area in square feet or square yards.");
  const area = number(
    row.jobSize ?? (row.jobSizeUnit ? item.quantity : useSelling ? item.quantity : item.area)
  ).mul(linear ? 1 : areaFactor(sourceUnit));
  const factor = number(row.factor);
  if (factor.lte(0)) throw Error("Coverage or weight factor must be greater than zero.");
  let quantity: Decimal;
  let formula: string;
  let numerator: Decimal;
  let denominator: Decimal;
  if (row.calculation === "coverage") {
    const coverageUnit = row.factorAreaUnit ?? "sq_ft";
    if (linear !== (coverageUnit === "lin_ft"))
      throw Error(
        "Coverage units must match the measurement: use linear feet for length or square units for area."
      );
    const coverage = factor.mul(linear ? 1 : areaFactor(coverageUnit));
    numerator = area;
    denominator = coverage;
    quantity = numerator.div(denominator);
    formula = `${area} ${linear ? "lin ft" : "sq ft"} ÷ ${coverage} ${
      linear ? "lin ft" : "sq ft"
    }/${row.unit}`;
  } else {
    const unit = row.unit.trim().toLowerCase();
    const divisor = ["ton", "tons", "us_ton"].includes(unit)
      ? 2000
      : ["lb", "lbs", "pound", "pounds"].includes(unit)
      ? 1
      : 0;
    if (!divisor) throw Error("Weight helpers require US tons or pounds.");
    if (row.calculation === "density") {
      const thickness = number(row.thickness);
      if (thickness.lte(0)) throw Error("Thickness must be greater than zero.");
      numerator = area.mul(thickness).mul(factor);
      denominator = new D(12).mul(divisor);
      quantity = numerator.div(denominator);
      formula = `${area} sq ft × (${thickness} in ÷ 12) × ${factor} lb/cu ft ÷ ${divisor}`;
    } else {
      numerator = area.mul(factor).mul(2000);
      denominator = new D(divisor);
      quantity = numerator.div(denominator);
      formula = `${area} sq ft × ${factor} US ton/sq ft (thickness included) × 2000 ÷ ${divisor}`;
    }
  }
  quantity = quantity.toDecimalPlaces(2);
  if (row.quantityOverride != null && row.quantityOverride !== "") {
    const override = number(row.quantityOverride).toDecimalPlaces(2);
    return {
      quantity: override,
      numerator: override,
      denominator: new D(1),
      formula: `Manual override; calculated: ${quantity.toDecimalPlaces(6)}; ${formula}`,
    };
  }
  return { quantity, formula, numerator: quantity, denominator: new D(1) };
}
export function calculateCustom(item: Custom, discountDollars = "0") {
  number(item.quantity);
  number(item.rate);
  number(item.markup);
  number(item.fixedPrice);
  const resolved = resolveHelpers(item, (r) => rowQuantity(item, r).quantity);
  const rows = resolved.rows.map((row) => {
    const result = rowQuantity(item, row);
    return {
      ...result,
      total: result.numerator
        .mul(number(row.rate))
        .div(result.denominator)
        .mul(100)
        .toDecimalPlaces(0),
    };
  });
  const enteredCost = rows.reduce((sum, row) => sum.plus(row.total), new D(0));
  const directCost =
    item.pricing || rows.length || item.pricingMethod === "cost_markup"
      ? enteredCost
      : number(item.quantity).mul(number(item.rate)).mul(100).toDecimalPlaces(0);
  const overhead = directCost
    .mul(number(item.overheadPercentage ?? "0"))
    .div(100)
    .toDecimalPlaces(0);
  const indirect = directCost
    .mul(number(item.indirectPercentage ?? "0"))
    .div(100)
    .toDecimalPlaces(0);
  const cost = directCost.plus(overhead).plus(indirect);
  const addedProfit =
    item.pricingMethod === "cost_markup"
      ? new D(0)
      : number(item.profitAmount ?? "0")
          .mul(100)
          .toDecimalPlaces(0);
  const basePrice =
    item.pricingMethod === "cost_markup"
      ? cost.plus(directCost.mul(number(item.markup)).div(100).toDecimalPlaces(0))
      : item.pricingMethod === "fixed"
      ? number(item.fixedPrice).mul(100).toDecimalPlaces(0)
      : number(item.quantity).mul(number(item.rate)).mul(100).toDecimalPlaces(0);
  let price = basePrice.plus(addedProfit);
  if (item.pricing) {
    const { mode, value, discount: savedDiscount } = item.pricing;
    if (!/^(?:\d{1,10}(?:\.\d{0,32})?|\.\d{1,32})$/.test(value))
      throw Error("Enter a nonnegative pricing value.");
    const entered = new D(value);
    const discount = number(discountDollars).mul(100).toDecimalPlaces(0);
    const originalDiscount = number(savedDiscount).mul(100).toDecimalPlaces(0);
    if (item.pricing.version !== 1) throw Error("Unsupported pricing version.");
    if (["profit", "margin"].includes(mode) && !rows.length)
      throw Error("Enter costs before applying a profit target.");
    switch (mode) {
      case "automatic":
        price = cost;
        break;
      case "rate":
        price = number(item.quantity)
          .mul(entered)
          .mul(100)
          .toDecimalPlaces(0)
          .plus(originalDiscount);
        break;
      case "fixed":
        price = entered.mul(100).toDecimalPlaces(0).plus(originalDiscount);
        break;
      case "profit":
        price = cost.plus(entered.mul(100).toDecimalPlaces(0)).plus(discount);
        break;
      case "margin":
        if (entered.gte(100)) throw Error("Margin must be less than 100%.");
        if (cost.isZero() && entered.gt(0))
          throw Error("With zero costs, use a profit amount instead of a margin target.");
        price = cost.mul(100).div(new D(100).minus(entered)).toDecimalPlaces(0).plus(discount);
        break;
      case "legacy_markup":
        price = cost.plus(directCost.mul(entered).div(100).toDecimalPlaces(0));
        break;
      default:
        throw Error("Choose a valid pricing calculation.");
    }
  }
  if (price.gt(2000000000) || cost.gt(2000000000))
    throw Error("Custom total exceeds the supported amount.");
  return {
    rows,
    cost,
    directCost,
    overhead,
    indirect,
    price,
    basePrice,
    addedProfit,
    margin:
      (item.pricing || item.pricingMethod === "cost_markup") && !rows.length
        ? null
        : price.minus(cost),
  };
}
export const money = (cents: Decimal | number | string) =>
  new D(cents).div(100).toNumber().toLocaleString("en-US", { style: "currency", currency: "USD" });
// GraphQL output contains derived fields and __typename; only send editable inputs.
export function customInput(item: Custom): Custom {
  const clean = blankCustom(item.markup);
  for (const key of Object.keys(clean) as (keyof Custom)[])
    if (key !== "rows" && item[key] != null) (clean as any)[key] = item[key];
  clean.pricingOverrideField =
    item.pricingOverrideField ?? (item.pricingMethod === "fixed" ? "total" : "none");
  clean.pricingBaseline = item.pricingBaseline
    ? {
        pricing: item.pricingBaseline.pricing ?? null,
        pricingMethod: item.pricingBaseline.pricingMethod,
        markup: item.pricingBaseline.markup,
        fixedPrice: item.pricingBaseline.fixedPrice,
        profitAmount: item.pricingBaseline.profitAmount ?? null,
      }
    : null;
  clean.rows = item.rows
    .map(normalizeTrucking)
    .map(
      (row) =>
        Object.fromEntries(
          [
            "rowId",
            "quantitySource",
            "hauling",
            "sourcePlantId",
            "sourcePlantName",
            "sourceType",
            "sourceId",
            "sourceName",
            "name",
            "category",
            "quantity",
            "hoursPerTruck",
            "truckingUnit",
            "trucksNeeded",
            "truckCapacity",
            "truckRateHours",
            "truckRateTons",
            "truckRateYards",
            "truckRateLoads",

            "jobSize",
            "jobSizeUnit",
            "unit",
            "rate",
            "calculation",
            "factor",
            "factorAreaUnit",
            "factorLabel",
            "thickness",
            "quantityOverride",
          ]
            .filter((key) => (row as any)[key] != null)
            .map((key) => [key, (row as any)[key]])
        ) as CostRow
    );
  return ensureRowIds(
    JSON.parse(JSON.stringify(clean), (key, value) => (key === "__typename" ? undefined : value))
  );
}

// Proposal discounts are applied after custom pricing, using integer cents.
export function customFinancials(item: Custom, discountDollars = "0") {
  const totals = calculateCustom(item, discountDollars);
  const discount = number(discountDollars).mul(100).toDecimalPlaces(0);
  if (discount.gt(totals.price)) throw Error("Discount cannot exceed the selling total.");
  const total = totals.price.minus(discount);
  const profit = total.minus(totals.cost);
  const costsKnown = item.pricing
    ? item.rows.length > 0
    : item.pricingMethod !== "cost_markup" || item.rows.length > 0;
  const margin = total.gt(0) ? profit.div(total).mul(100) : new D(0);
  return { ...totals, discount, total, costsKnown, profit, marginPercentage: margin };
}
export const decimalDollars = (cents: string) => new D(cents).div(100).toFixed(2);
export const priceBeforeDiscount = (total: string, discount: string, profit = "0") => {
  const base = number(total).plus(number(discount)).minus(number(profit));
  if (base.lt(0)) throw Error("Total plus discount cannot be less than entered profit.");
  return base.toFixed(2);
};

export const formatDollarAmount = (value: string) => {
  if (!/^-?(?:\d+(?:\.\d*)?|\.\d+)$/.test(value)) return value;
  const [whole, fraction] = new D(value).toFixed(2).split(".");
  return `${whole.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}.${fraction}`;
};

// Profit and margin targets describe the displayed amounts after proposal discounts.
export function pricingFromTarget(
  item: Custom,
  discountDollars: string,
  target: "profit" | "margin",
  value: string
): Partial<Custom> {
  const totals = calculateCustom(item);
  const base = totals.cost;
  const discount = number(discountDollars).mul(100).toDecimalPlaces(0);
  const entered = number(value);
  if (target === "margin" && entered.gte(100)) throw Error("Margin must be less than 100%.");
  if (target === "margin" && base.isZero() && entered.gt(0))
    throw Error("Enter a base cost before setting a margin.");
  const netProfit =
    target === "profit"
      ? entered.mul(100).toDecimalPlaces(0)
      : base.mul(entered).div(new D(100).minus(entered)).toDecimalPlaces(0);
  const grossProfit = netProfit.plus(discount);
  if (item.pricingMethod === "cost_markup") {
    if (totals.directCost.isZero() && grossProfit.gt(0))
      throw Error("Enter costs before setting a profit or margin with cost-based pricing.");
    return {
      markup: totals.directCost.isZero()
        ? "0"
        : grossProfit.div(totals.directCost).mul(100).toDecimalPlaces(8).toString(),
    };
  }
  const addedProfit = grossProfit.plus(totals.cost).minus(totals.basePrice);
  if (addedProfit.lt(0))
    return {
      pricingMethod: "fixed",
      fixedPrice: totals.cost.plus(grossProfit).div(100).toFixed(2),
      profitAmount: "0",
    };
  return { profitAmount: addedProfit.div(100).toFixed(2) };
}

// Keep the first valid override's settings, not the previous keystroke's price.
export function capturePricingBaseline(item: Custom): PricingBaseline {
  return (
    item.pricingBaseline ?? {
      pricing: item.pricing ?? null,
      pricingMethod: item.pricingMethod,
      markup: item.markup,
      fixedPrice: item.fixedPrice,
      profitAmount: item.profitAmount ?? null,
    }
  );
}
export function resetCustomPricing(item: Custom): Custom {
  if (!item.pricingBaseline) return item;
  return { ...item, ...item.pricingBaseline, pricingBaseline: null, pricingOverrideField: "none" };
}

export const truckingUnits: TruckingUnit[] = ["Hours", "Tons", "Yards", "Loads"];
export function normalizeTrucking(row: CostRow): CostRow {
  if (row.category !== "trucking" || row.truckingUnit) return row;
  return {
    ...row,
    truckingUnit: "Hours",
    trucksNeeded: row.quantity,
    quantity: row.hoursPerTruck ?? "1",
    unit: "Hours",
    truckRateHours: row.rate,
  };
}
export function changeTruckingUnit(row: CostRow, unit: TruckingUnit): CostRow {
  const old = normalizeTrucking(row);
  const rates = { ...old, [`truckRate${old.truckingUnit}`]: old.rate };
  return {
    ...rates,
    truckingUnit: unit,
    unit,
    truckCountDefault: unit === "Hours" && !old.trucksNeeded ? true : old.truckCountDefault,
    trucksNeeded:
      unit === "Hours" ? old.trucksNeeded || "1" : old.truckCountDefault ? null : old.trucksNeeded,
    rate: (rates as any)[`truckRate${unit}`] ?? "",
  };
}

// Upgrade only on editing. Stored historical snapshots retain their original calculation.
export function upgradePricing(item: Custom, discount = "0"): Custom {
  if (item.pricing) return item;
  const old = calculateCustom(item);
  const net = old.price.minus(number(discount).mul(100));
  const q = number(item.quantity);
  const pricing: PricingControl =
    item.pricingMethod === "cost_markup"
      ? { version: 1, mode: "legacy_markup", value: item.markup, discount: "0" }
      : item.pricingMethod === "selling_rate" && q.gt(0)
      ? { version: 1, mode: "rate", value: net.div(100).div(q).toFixed(32), discount }
      : { version: 1, mode: "fixed", value: net.div(100).toFixed(2), discount };
  const baseline = item.pricingBaseline
    ? capturePricingBaseline(
        upgradePricing({ ...item, ...item.pricingBaseline, pricingBaseline: null }, discount)
      )
    : null;
  return { ...item, pricing, pricingBaseline: baseline, profitAmount: "0" };
}
export function manualPricing(
  item: Custom,
  mode: "rate" | "fixed",
  value: string,
  discount: string
): Partial<Custom> {
  const amount = number(value).toString();
  return {
    pricing: { version: 1, mode, value: amount, discount: number(discount).toString() },
    pricingMethod: mode === "rate" ? "selling_rate" : "fixed",
    profitAmount: "0",
    pricingOverrideField: mode === "rate" ? "rate" : "total",
    pricingBaseline: capturePricingBaseline(item),
  };
}
export function changeCustom(
  item: Custom,
  patch: Partial<Custom>,
  discount: string,
  lastValidTotal?: string
): Custom {
  let next = { ...item, ...patch };
  if (
    patch.rows &&
    item.rows.length &&
    !patch.rows.length &&
    item.pricing &&
    ["automatic", "profit", "margin", "legacy_markup"].includes(item.pricing.mode)
  ) {
    let total = lastValidTotal;
    try {
      total = customFinancials(item, discount).total.div(100).toFixed(2);
    } catch (error) {
      if (total === undefined) throw error;
    }
    next = { ...next, ...manualPricing(item, "fixed", total, discount), pricingBaseline: null };
  }
  if (
    patch.rows &&
    !patch.rows.length &&
    ["profit", "margin"].includes(next.pricingBaseline?.pricing?.mode ?? "")
  ) {
    next = { ...next, pricingBaseline: null };
  }
  return ensureRowIds(next);
}
export function pricingStatus(item: Custom): string {
  switch (item.pricing?.mode) {
    case "automatic":
      return "Price follows costs";
    case "profit":
      return `Profit target: ${formatDollarAmount(item.pricing.value)} — price follows costs`;
    case "margin":
      return `Margin target: ${item.pricing.value}% — price follows costs`;
    case "legacy_markup":
      return "Price follows costs and saved markup";
    case "rate":
      return "Selling price entered manually";
    default:
      return "Total entered manually";
  }
}

/** Empty releases only the active target; explicit zero remains a linked target. */
export function editProfitTarget(
  item: Custom,
  mode: "profit" | "margin",
  raw: string,
  discount: string
): Partial<Custom> {
  if (!raw.trim()) {
    if (item.pricing?.mode !== mode) return {};
    const total = customFinancials(item, discount).total.div(100).toFixed(2);
    return {
      ...manualPricing(item, "fixed", total, discount),
      pricingOverrideField: "none",
      pricingBaseline: null,
    };
  }
  const value = number(raw).toString();
  const patch: Partial<Custom> = {
    pricing: { version: 1, mode, value, discount: "0" },
    profitAmount: "0",
    pricingOverrideField: "none",
    pricingBaseline: null,
  };
  customFinancials({ ...item, ...patch }, discount);
  return patch;
}
